import { useTheme } from '@mui/system';
import { MaskStylesObj } from '@reactour/mask';
import { PopoverStylesObj } from '@reactour/popover';
import { TourProvider as ReactTourProvider, StylesObj, useTour } from '@reactour/tour';
import { MicroTaskConfigType, MicrotaskType } from 'ihub/common';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useComponentLoaded } from '../component-loaded/component-loaded-provider';
import { getTaskGeneralTourSteps, getTourCompletedStep } from './component-tours';
import { getMicroTaskAdvancedTour } from './micro-task-tour-helper';
import { getEditIntegrationTemplateTourSteps } from './route-tours';
import { AdvancedTourDefinition, ComponentTourId, TourId } from './shared-types';
import getTaskTourRegistry from './task-tour-registry';
import { useTourRegistry } from './tour-registry';
import getComponentTourRegistry from './component-tour-registry';

type TourContextValue = {
	startTour: (tourId: TourId, runtime?: { config: MicroTaskConfigType | undefined }) => void;
	isTourActive: boolean;
	goToNextTour: () => void;
	startRouteTour: () => void;
	currentTour: TourId | undefined;
	buttonLabel: string;
	routeTour: TourId | undefined;
	startMicroTaskAdvancedTour: (
		taskType: MicrotaskType,
		microTaskConfigType: MicroTaskConfigType | undefined
	) => void;
	startComponentTour: (tourId: ComponentTourId, microTaskConfigType?: MicroTaskConfigType) => void;
	updateTourRuntime: (runtime?: { config: MicroTaskConfigType | undefined }) => void;
};

const TourContext = React.createContext<TourContextValue | null>(null);

export const useTourProvider = () => {
	const ctx = useContext(TourContext);
	if (!ctx) throw new Error('useTourProvider must be used inside TourProvider');
	return ctx;
};

const TourProvider = ({ children }: { children: React.ReactNode }) => {
	const tour = useTour();
	const { palette } = useTheme();
	const { componentLoaded, componentId } = useComponentLoaded();
	const { routeTour, buttonLabel } = useTourRegistry();

	const [currentTour, setCurrentTour] = useState<TourId | undefined>();

	const defaultStyles: (StylesObj & PopoverStylesObj & MaskStylesObj) | undefined = useMemo(
		() => ({
			popover: base => ({
				...base,
				backgroundColor: palette.common.elevationOverlays.mainContainer.even,
				color: palette.text.primary,
				borderRadius: 12,
				padding: 16,
				boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
				left: '20px',
			}),
			arrow: base => ({
				...base,
				color: '#1e1e1e',
			}),
			close: base => ({
				...base,
				color: '#fff',
				top: '10px',
				right: '10px',
			}),
			highlightedArea: base => ({
				...base,
				borderRadius: 16,
				pointerEvents: 'none',
			}),
		}),
		[palette]
	);

	const advancedTourRegistry: Record<TourId, AdvancedTourDefinition> = useMemo(
		() => ({
			...getEditIntegrationTemplateTourSteps({ defaultStyles }),
			...getTaskTourRegistry({ defaultStyles }),
			...getComponentTourRegistry({ defaultStyles }),
			taskBasics: {
				start: 'task-type',
				steps: [
					...getTaskGeneralTourSteps({ defaultStyles }),
					getTourCompletedStep({ defaultStyles, selector: 'task-drawer' }),
				],
				autoStartNext: false,
			},
		}),
		[defaultStyles]
	);

	// Helper: resolve steps for a TourId
	const resolveTour = useCallback(
		(tourId: TourId, runtime?: { config: MicroTaskConfigType | undefined }) => {
			const definition = advancedTourRegistry[tourId];
			if (!definition) return null;

			const steps =
				typeof definition.steps === 'function' ? definition.steps(runtime) : definition.steps;

			const startIndex = definition.start ? steps.findIndex(s => s.id === definition.start) : 0;

			return {
				steps,
				startIndex: startIndex >= 0 ? startIndex : 0,
				nextTour: definition.nextTour,
			};
		},
		[advancedTourRegistry]
	);

	//Start a tour
	const startTour = useCallback(
		(tourId: TourId, runtime?: { config: MicroTaskConfigType | undefined }) => {
			const resolved = resolveTour(tourId, runtime);
			if (!resolved) return;

			setCurrentTour(tourId);

			tour.setSteps?.(resolved.steps);
			tour.setCurrentStep(resolved.startIndex);
			tour.setIsOpen(true);
		},
		[resolveTour, tour]
	);

	const startRouteTour = () => {
		if (routeTour) startTour(routeTour);
	};

	//Move to next TourId manually (event-based)
	const goToNextTour = useCallback(() => {
		if (!currentTour) return;

		const definition = advancedTourRegistry[currentTour];
		if (!definition?.nextTour) return;

		const resolved = resolveTour(definition.nextTour);
		if (!resolved) return;

		setTimeout(() => {
			setCurrentTour(definition.nextTour);

			tour.setSteps?.(resolved.steps);
			tour.setCurrentStep(resolved.startIndex);
			tour.setIsOpen(true);
		}, 1000);
	}, [currentTour, resolveTour, tour, advancedTourRegistry]);

	// Clear currentTour when tour is closed
	useEffect(() => {
		if (!tour.isOpen && currentTour) {
			const resolvedTour = resolveTour(currentTour);
			if (!resolvedTour) return;
			const isLastStep = tour.currentStep === resolvedTour.steps.length - 1;
			if (resolvedTour.nextTour && isLastStep) goToNextTour();

			setCurrentTour(undefined);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tour.isOpen, currentTour, tour.currentStep]);

	useEffect(() => {
		if (!currentTour) return;
		const definition = advancedTourRegistry[currentTour];
		if (!definition) return;

		if (componentLoaded && currentTour && definition.nextTour === componentId) goToNextTour();

		if (!componentLoaded && currentTour === componentId) {
			tour.setIsOpen(false);
			tour.setSteps?.([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [componentLoaded, currentTour, componentId, advancedTourRegistry]);

	const startMicroTaskAdvancedTour = useCallback(
		(taskType: MicrotaskType, config: MicroTaskConfigType | undefined) => {
			const tourId = getMicroTaskAdvancedTour(taskType);
			if (!tourId) return;

			startTour(tourId, { config });
		},
		[startTour]
	);

	const startComponentTour = useCallback(
		(tourId: ComponentTourId, config: MicroTaskConfigType | undefined) => {
			if (!tourId) return;

			startTour(tourId, { config });
		},
		[startTour]
	);

	const updateTourRuntime = useCallback(
		(runtime?: { config: MicroTaskConfigType | undefined }) => {
			if (!currentTour) return;
			const resolved = resolveTour(currentTour, runtime);
			if (!resolved) return;
			tour.setSteps?.(resolved.steps);
		},
		[currentTour, resolveTour, tour]
	);

	const contextValue: TourContextValue = {
		startTour,
		goToNextTour,
		startMicroTaskAdvancedTour,
		updateTourRuntime,
		currentTour,
		isTourActive: tour.isOpen,
		startRouteTour,
		buttonLabel,
		routeTour,
		startComponentTour,
	};

	return <TourContext.Provider value={contextValue}>{children}</TourContext.Provider>;
};

export default function TourProviderWrapper({ children }: { children: React.ReactNode }) {
	return (
		<ReactTourProvider
			steps={[]}
			showBadge={false}
			disableKeyboardNavigation={['esc']}
			onClickMask={click => {
				/* left empty intentionally to prevent closing when clicking on the backdrop */
			}}
		>
			<TourProvider>{children}</TourProvider>
		</ReactTourProvider>
	);
}
